import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm } from 'react-hook-form';
import './style.scss';

import { useMutation } from '@apollo/client';
import {
  ADD_SCHOOL_TO_CART,
  ADD_SCHOOL_TO_CART_TYPE,
  ADD_SCHOOL_TO_CART_VARIABLES_TYPE,
} from '@global/requests/checkout/ADD_SCHOOL_TO_CART';

import { ButtonCTA } from '@global/components/UI Elements/ButtonCTA';
import { StateSelectInput } from '@src/global/components/ActiveForm/Elements/SelectState';
import { SelectSchoolInput } from '@global/components/ActiveForm/Elements/SelectSchool';
import { Radio } from '@global/components/ActiveForm/Elements/Radio';
import { Title } from '@pages/checkout/components/Title';

import {
  getSchoolByFiceId,
  getSchoolsByStateCode,
  getStatesWithSchools,
  StatesWithSchools,
  findStateInfoBySchoolId,
} from '@global/utils/SchoolService';
import { useYupValidationResolver } from '@global/components/ActiveForm/utils';
import { SchoolStepSchema } from './rules';
import { CHECKOUT_SCHOOL_STEP } from '@pages/checkout/consts';

import { useAppSelector } from '@global/store/hooks';
import { cartSelector } from '@global/store/slices/cart/selectors';
import analytic from '@global/utils/AdobeAnalytics';

interface SchoolStepProps {
  activeStep: number;
  handleNextStep: () => void;
  stepNumber?: string;
  handleReturnToCurrentStep: () => void;
}

export const SchoolStep = ({
  activeStep,
  stepNumber,
  handleNextStep,
  handleReturnToCurrentStep,
}: SchoolStepProps) => {
  const { t } = useTranslation();
  const [isWithSchool, setIsWithSchool] = useState<boolean | null>(null);

  const [statesWithSchools, setStatesWithSchools] = useState<
    StatesWithSchools[]
  >([]);

  const cartState = useAppSelector(cartSelector);
  const [addSchoolToOrder] = useMutation<
    ADD_SCHOOL_TO_CART_TYPE,
    ADD_SCHOOL_TO_CART_VARIABLES_TYPE
  >(ADD_SCHOOL_TO_CART);

  const resolverSchema = SchoolStepSchema();
  const rulesResolver = useYupValidationResolver(resolverSchema);

  const methods = useForm({
    mode: 'all',
    defaultValues: { state: '', school: '', country: 'US' },
    resolver: rulesResolver,
  });
  const currentCountry = methods.watch('country');
  const schoolFiceId = methods.watch('school');

  const isCurrentStep = activeStep === CHECKOUT_SCHOOL_STEP;

  const selectedState = methods.watch('state');

  const schools = useMemo(() => {
    return getSchoolsByStateCode(
      statesWithSchools,
      `${currentCountry}-${selectedState}`
    );
  }, [selectedState, statesWithSchools]);

  const school = useMemo(
    () => getSchoolByFiceId(schools, schoolFiceId),
    [schools, schoolFiceId]
  );

  const isDisabledCTA =
    isWithSchool === null ||
    (isWithSchool && (!selectedState || (schools.length && !schoolFiceId)));

  const schoolsNotAvailable = selectedState && !schools.length;

  const handleSubmit = useCallback(async () => {
    const currentSchoolFiceId = methods.getValues('school');

    const currentSchool = schools.find(
      (school) => school.schoolFiceId === currentSchoolFiceId
    );
    const schoolName = currentSchool?.schoolName || '';
    const schoolFiceId = currentSchool?.schoolFiceId || '';

    await addSchoolToOrder({
      variables: {
        uid: cartState.cartId,
        schoolName: isWithSchool && schoolName ? schoolName : '',
        schoolFiceId: isWithSchool && schoolFiceId ? schoolFiceId : '',
      },
    });

    analytic.dataLayerSchoolInfoClick();

    handleNextStep();
  }, [cartState.cartId, schools, isWithSchool]);

  const handleSelect = useCallback((value: boolean) => {
    setIsWithSchool(value);
  }, []);

  useEffect(() => {
    async function init() {
      const states = await getStatesWithSchools();
      setStatesWithSchools(states);
    }

    init();
  }, []);

  useEffect(() => {
    if (cartState.schoolFiceId) {
      const stateInfo = findStateInfoBySchoolId(cartState.schoolFiceId);
      const stateCode = stateInfo.stateCode || '';
      const stateSchools = stateInfo.schools || [];
      const [country = '', state = ''] = stateCode.split('-');
      const school = stateSchools.find(
        (s) => s.schoolFiceId === cartState.schoolFiceId
      );

      methods.setValue('country', country);
      methods.setValue('state', state);
      methods.setValue('school', school?.schoolFiceId || '');

      setIsWithSchool(true);
      handleNextStep();
    }
  }, [cartState.schoolFiceId]);

  return (
    <>
      <Title
        numberText={stepNumber}
        title={t(`checkout.schoolStep.title`)}
        current={CHECKOUT_SCHOOL_STEP}
        active={activeStep}
        onClick={handleReturnToCurrentStep}
      >
        {activeStep > CHECKOUT_SCHOOL_STEP ? (
          <div className="checkout__selected-values">
            {school && isWithSchool === true ? (
              <div>{school.schoolName}</div>
            ) : (
              <div>No School selected</div>
            )}
          </div>
        ) : null}
      </Title>
      <div hidden={!isCurrentStep} className="schoolStep__body">
        <div className="schoolStep__question">
          <h3>{t('checkout.schoolStep.question')}</h3>
        </div>
        <div className="schoolStep__btns-wrapper">
          <Radio
            onChange={() => handleSelect(true)}
            id="true"
            name="withSchool"
            checked={isWithSchool === true}
          >
            Yes
          </Radio>
          <Radio
            onChange={() => handleSelect(false)}
            id="false"
            name="withSchool"
            checked={isWithSchool === false}
          >
            No
          </Radio>
        </div>

        <FormProvider {...methods}>
          <form className="schoolStep__form" hidden={!isWithSchool}>
            <StateSelectInput
              parentFieldName="country"
              fieldName="state"
              selectedState=""
            />
            <SelectSchoolInput
              fieldName={schoolsNotAvailable ? '' : 'school'} // hack to extract component from validation flow and do not patch yup schema
              schools={schools}
              disabled={schoolsNotAvailable}
              placeholder={
                schoolsNotAvailable
                  ? t('checkout.schoolStep.noAvailableSchools')
                  : t('activeForm.schoolSelect')
              }
            />
          </form>
        </FormProvider>

        <ButtonCTA
          className={'schoolStep__cta'}
          variant={'primary'}
          disabled={isDisabledCTA}
          onClick={handleSubmit}
        >
          {t('checkout.schoolStep.action')}
        </ButtonCTA>
      </div>
    </>
  );
};
